import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Copy, Check, KeyRound, Ban, Clock } from 'lucide-react';
import { tokensAPI } from '../api';
import { Card, Button, Input, Textarea, Select, Table, Modal, PageHeader, Alert, useSort } from '../components/UI';

const PERMISSION_LEVELS = ['read', 'write', 'full'];

const PERMISSION_COLORS = {
  read:  { bg: 'rgba(59,130,246,0.1)',  fg: 'var(--accent-blue)',   bd: 'rgba(59,130,246,0.25)' },
  write: { bg: 'rgba(245,158,11,0.12)', fg: 'var(--accent-orange)', bd: 'rgba(245,158,11,0.3)' },
  full:  { bg: 'rgba(239,68,68,0.12)',  fg: 'var(--accent-red)',    bd: 'rgba(239,68,68,0.3)' },
};

function PermissionBadge({ level }) {
  const c = PERMISSION_COLORS[level] || PERMISSION_COLORS.read;
  return (
    <span style={{ fontSize: 11, padding: '2px 9px', borderRadius: 20, background: c.bg, color: c.fg, border: `1px solid ${c.bd}`, fontWeight: 500 }}>
      {level}
    </span>
  );
}

function StatusPill({ token }) {
  const revoked = !!token.revoked_at;
  const expired = !token.permanent && token.expires_at && new Date(token.expires_at) < new Date();
  let label = 'active', color = 'var(--accent-green)', bg = 'rgba(16,185,129,0.1)', bd = 'rgba(16,185,129,0.3)';
  if (revoked) { label = 'revoked'; color = 'var(--accent-red)'; bg = 'rgba(239,68,68,0.1)'; bd = 'rgba(239,68,68,0.3)'; }
  else if (expired) { label = 'expired'; color = 'var(--text-muted)'; bg = 'rgba(148,163,184,0.12)'; bd = 'var(--border)'; }
  return (
    <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, background: bg, color, border: `1px solid ${bd}` }}>
      {label === 'active' ? '● ' : '○ '}{label}
    </span>
  );
}

const EMPTY_FORM = { name: '', description: '', permission_level: 'read', never_expires: true, expires_at: '' };

export default function ApiTokens() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);

  const [newToken, setNewToken] = useState(null); // raw token shown once after create
  const [copied, setCopied] = useState(false);

  const [revokeConfirm, setRevokeConfirm] = useState(null);

  const { sort, onSortChange } = useSort();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await tokensAPI.list();
      setRows(res.tokens || []);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, []);

  // ApiTokens gak dipaginasi backend (jumlahnya kecil per admin) — sort dilakukan
  // di frontend langsung terhadap hasil fetch, bukan lewat param ?sort= ke server.
  const sortedRows = React.useMemo(() => {
    if (!sort) return rows;
    const { key, dir } = sort;
    const arr = [...rows].sort((a, b) => {
      const av = a[key], bv = b[key];
      if (av === bv) return 0;
      if (av === null || av === undefined) return 1;
      if (bv === null || bv === undefined) return -1;
      return av > bv ? 1 : -1;
    });
    return dir === 'desc' ? arr.reverse() : arr;
  }, [rows, sort]);

  useEffect(() => { load(); }, [load]);

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setFormError(null);
    setNewToken(null);
    setCopied(false);
    setModalOpen(true);
  };

  const handleSave = async () => {
    setFormError(null);
    if (!form.name.trim()) return setFormError('Nama token wajib diisi.');
    if (!form.never_expires && !form.expires_at) return setFormError('Tentukan tanggal expired, atau pilih "Tidak pernah expired".');

    setSaving(true);
    try {
      const payload = {
        name: form.name,
        description: form.description,
        permission_level: form.permission_level,
        never_expires: form.never_expires,
      };
      if (!form.never_expires) payload.expires_at = new Date(form.expires_at).toISOString();
      const res = await tokensAPI.create(payload);
      setNewToken(res.token);
      load();
    } catch (e) { setFormError(e.message); }
    finally { setSaving(false); }
  };

  const handleRevoke = async () => {
    try {
      await tokensAPI.revoke(revokeConfirm.id);
      setSuccess(`Token "${revokeConfirm.name}" dicabut.`);
      setRevokeConfirm(null);
      load();
    } catch (e) { setError(e.message); setRevokeConfirm(null); }
  };

  const copyToken = async () => {
    try {
      await navigator.clipboard.writeText(newToken);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard API mungkin diblokir; biarkan user copy manual
    }
  };

  const closeModal = () => {
    setModalOpen(false);
    setNewToken(null);
  };

  const columns = [
    {
      key: 'name', label: 'Name', sortable: true, render: (v, row) => (
        <div>
          <div style={{ fontWeight: 500, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 6 }}>
            <KeyRound size={13} color="var(--text-muted)" /> {v}
          </div>
          {row.description && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{row.description}</div>}
        </div>
      )
    },
    {
      key: 'prefix', label: 'Token', render: v => (
        <span className="mono" style={{ fontSize: 12, color: 'var(--text-mono)' }}>{v}••••••••••••</span>
      )
    },
    { key: 'permission_level', label: 'Permission', sortable: true, render: v => <PermissionBadge level={v} /> },
    { key: 'status', label: 'Status', render: (v, row) => <StatusPill token={row} /> },
    {
      key: 'expires_at', label: 'Expired', render: (v, row) => (
        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
          {row.permanent || !v ? 'Tidak pernah' : new Date(v).toLocaleString('id-ID')}
        </span>
      )
    },
    { key: 'last_used_at', label: 'Last Used', sortable: true, render: v => <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{v ? new Date(v).toLocaleString('id-ID') : '—'}</span> },
    {
      key: 'id', label: '', render: (id, row) => {
        if (row.revoked_at) return null;
        return (
          <button onClick={() => setRevokeConfirm(row)}
            style={{ background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '5px 10px', cursor: 'pointer', color: 'var(--accent-red)', fontSize: 12, display: 'flex', alignItems: 'center', gap: 5 }}>
            <Ban size={12} /> Cabut
          </button>
        );
      }
    },
  ];

  return (
    <div className="animate-in">
      <PageHeader
        title="API Token"
        subtitle={`${rows.length} token milik akun Anda`}
        actions={
          <Button onClick={openCreate}><Plus size={14} /> Generate Token</Button>
        }
      />

      {error && <Alert type="error" message={error} onClose={() => setError(null)} />}
      {success && <Alert type="success" message={success} onClose={() => setSuccess(null)} />}

      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '10px 12px', background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.2)', borderRadius: 'var(--radius)', fontSize: 12, color: 'var(--text-secondary)', marginBottom: 16 }}>
        <Clock size={14} style={{ flexShrink: 0, marginTop: 1, color: 'var(--accent-blue)' }} />
        Token bertindak sebagai akun Anda (impersonation) — akses tenant &amp; resource ikut role dan tenant_ids Anda. <strong>permission_level</strong> hanya membatasi dari segi read/write/full di dalam batas itu.
      </div>

      <Card style={{ padding: 0 }}>
        <Table columns={columns} data={sortedRows} loading={loading} sort={sort} onSortChange={onSortChange} emptyMessage="Belum ada API token" />
      </Card>

      {/* Create Modal */}
      <Modal open={modalOpen} onClose={closeModal} title={newToken ? 'Token Berhasil Dibuat' : 'Generate API Token'} width={460}>
        {newToken ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ padding: '10px 12px', background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 'var(--radius)', fontSize: 12, color: 'var(--accent-orange)' }}>
              ⚠️ Simpan token ini sekarang. Token mentah hanya ditampilkan sekali dan tidak bisa dilihat lagi setelah modal ini ditutup.
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <code style={{
                flex: 1, background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 'var(--radius)',
                padding: '10px 12px', fontSize: 12, color: 'var(--text-mono)', wordBreak: 'break-all', fontFamily: 'var(--font-mono)',
              }}>{newToken}</code>
              <button onClick={copyToken} title="Copy" style={{
                background: copied ? 'var(--accent-green)' : 'var(--bg-input)', border: '1px solid var(--border)',
                borderRadius: 'var(--radius)', padding: 9, cursor: 'pointer', color: copied ? '#fff' : 'var(--text-secondary)',
                display: 'flex', flexShrink: 0,
              }}>
                {copied ? <Check size={15} /> : <Copy size={15} />}
              </button>
            </div>
            <Button onClick={closeModal}>Selesai</Button>
          </div>
        ) : (
          <>
            {formError && <Alert type="error" message={formError} />}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <Input label="Nama Token *" value={form.name} onChange={e => f('name', e.target.value)} placeholder="CI/CD Pipeline" />
              <Textarea label="Description" value={form.description} onChange={e => f('description', e.target.value)} placeholder="Untuk integrasi GitLab CI" rows={3} />
              <Select label="Permission Level *" value={form.permission_level} onChange={e => f('permission_level', e.target.value)}>
                {PERMISSION_LEVELS.map(p => <option key={p} value={p}>{p}</option>)}
              </Select>

              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--text-secondary)', cursor: 'pointer' }}>
                <input type="checkbox" checked={form.never_expires} onChange={e => f('never_expires', e.target.checked)} style={{ accentColor: 'var(--accent-blue)' }} />
                Tidak pernah expired
              </label>

              {!form.never_expires && (
                <Input label="Expired Pada *" type="datetime-local" value={form.expires_at} onChange={e => f('expires_at', e.target.value)} />
              )}
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
              <Button variant="ghost" onClick={closeModal}>Batal</Button>
              <Button onClick={handleSave} loading={saving}>Generate</Button>
            </div>
          </>
        )}
      </Modal>

      {/* Revoke confirm */}
      <Modal open={!!revokeConfirm} onClose={() => setRevokeConfirm(null)} title="Cabut Token" width={380}>
        <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 16 }}>
          Yakin ingin mencabut token <strong style={{ color: 'var(--accent-red)' }}>{revokeConfirm?.name}</strong>? Aplikasi yang memakai token ini akan langsung berhenti berfungsi.
        </p>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button variant="ghost" onClick={() => setRevokeConfirm(null)}>Batal</Button>
          <Button variant="danger" onClick={handleRevoke}>Cabut Token</Button>
        </div>
      </Modal>
    </div>
  );
}